import { useMemo, useState, useEffect, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import {
  BarChart, Bar, Line, Area, AreaChart, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ReferenceArea, Cell
} from "recharts";
import { useTheme, getColors } from "../ThemeContext";
import { useAIAdvice } from "../useAIAdvice";
import PlotSummary from "../components/PlotSummary";

const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";

const C = {
  bg:"#050b14",panel:"#080f1e",card:"#0b1525",border:"#0f2040",border2:"#162a4a",
  cyan:"#00d4ff",teal:"#00ffcc",amber:"#ffb84d",pink:"#ff4da6",purple:"#a855f7",
  blue:"#3b9eff",red:"#ff3d5a",green:"#00ff88",orange:"#ff7c2a",
  text:"#e2eaf8",muted:"#4a6080",muted2:"#2a3f60",
};
/* S = series colours used for every pressure plot, same palette as the Benchmarking page plots
   (theme colours + its pale-turquoise "pink", dark cyan and dark indigo accents) */
const S = { cyan:"", teal:"", amber:"", pink:"", purple:"", blue:"", red:"", green:"", orange:"", lime:"", darkCyan:"", darkIndigo:"" };
function applyTheme(colors, isDark) {
  Object.assign(C, colors);
  Object.assign(S, {
    cyan:colors.cyan, teal:colors.teal, green:colors.green, amber:colors.amber, orange:colors.orange,
    pink: isDark ? "#afeeee" : colors.pink,
    purple:colors.purple, blue:colors.blue, red:colors.red, lime:colors.lime,
    darkCyan:"#0b647d", darkIndigo:"#4a4e8d",
  });
}

const CHART_MARGIN_DEFAULT = { top: 8, right: 14, left: -10, bottom: 52 };
const CHART_MARGIN_HISTO   = { top: 8, right: 14, left: -10, bottom: 52 };
const ROW_MARGIN = { top: 8, right: 8, left: 8, bottom: 0 };   // identical on every chart so plot areas line up
const AXIS_W  = 84;     // left + right axis width (tick labels + axis title)
const XAXIS_H = 58;     // single-line cycle tick + axis title
const Y_PAD   = ["dataMin - 3", "dataMax + 3"];
const BAND_OP   = 0.22;   // opacity of the cycle-stage background columns (legend swatches use the same value)
const BAND_LEG_H = 40;   // extra height reserved for the background legend under a chart
const HERO_H  = 330;    // main chart
const MID_H   = 230;    // supporting charts

/* axis-title helper: left axis reads bottom→top, right axis top→bottom */
const yLbl = (text, color, right) => ({
  value:text, angle:right ? 90 : -90, position:right ? "insideRight" : "insideLeft",
  fill:color===C.muted ? C.text : color, fontSize:11, fontFamily:MONO, style:{ textAnchor:"middle" },
});

function cycleTickFmt(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!isNaN(n)) return `C${n}`;
  return String(v);
}
function CYCLE_XAXIS_PROPS() {
  return { tick:{ fill:C.muted, fontSize:9, fontFamily:MONO }, tickLine:false, tickFormatter:cycleTickFmt };
}

/* ══ Shut-in rules ═════════════════════════════════════════════════════════
   Buildup_n = CsgAtOpen_n − CsgAtClose_(n−1)          casing gain during shut-in      [psi]
   Rate_n    = Buildup_n / TimeOffTotal_n                                             [psi/min]
   NEGATIVE  : Buildup_n < neg                          → CRITICAL (casing LOST pressure while shut in)
   FLATTENING: TimeOffTotal_n > longX × median(TimeOffTotal)  AND  Rate_n < flat
                                                         → WARNING (extra shut-in = lost flow time)
   Values come from the project brief and are applied internally (not exposed on the frontend).   */
const DEFAULT_RULES = { neg:-2, flat:0.15, longX:1.25 };

const PHASE_LABEL = { ok:"Normal", flat:"Flattening", negative:"Negative buildup" };
const phaseColor  = k => (k==="flat" ? S.purple : k==="negative" ? S.red : S.green);

/* Background drawn behind the charts, one column per cycle:
   bottom→top = shut-in · plunger rise · after-flow (share of that cycle's time),
   plus a full-height tint when the cycle is flagged (flattening / negative). */
function StageBand({ x, y, width, height, payload }) {
  if (!payload || !(height > 0)) return null;
  const sh = payload._shutMin || 0, ri = payload._riseMin || 0, af = payload._afterMin || 0;
  const tot = sh + ri + af;
  let yy = y + height;
  const segs = tot > 0 ? [[sh, S.amber], [ri, S.cyan], [af, S.green]] : [];
  return (
    <g>
      {segs.map(([v, c], i) => {
        const h = (v / tot) * height; yy -= h;
        return <rect key={i} x={x} y={yy} width={width} height={h} fill={c} fillOpacity={BAND_OP}/>;
      })}
      {payload._phase && payload._phase !== "ok" && (
        <rect x={x} y={y} width={width} height={height} fill={phaseColor(payload._phase)} fillOpacity={0.14}/>
      )}
    </g>
  );
}

function buildContext(tsData, rules) {
  const shuts = tsData.map(d=>d.TimeOffTotal).filter(v=>typeof v==="number"&&v>0).sort((a,b)=>a-b);
  const medShut = shuts.length ? shuts[Math.floor(shuts.length/2)] : null;
  let cum = 0;
  return tsData.map((d,i)=>{
    const prev = tsData[i-1];
    const shut = d.TimeOffTotal;
    let buildup = null, rate = null, phase = "ok";
    if (prev && typeof d.CsgAtOpen==="number" && typeof prev.CsgAtClose==="number") buildup = d.CsgAtOpen - prev.CsgAtClose;
    if (buildup!=null && typeof shut==="number" && shut>0) rate = buildup/shut;
    if (buildup!=null && buildup < rules.neg) phase = "negative";
    else if (rate!=null && medShut && shut > medShut*rules.longX && rate < rules.flat) phase = "flat";
    if (typeof d.VolCycleTotal==="number" && d.VolCycleTotal>0) cum += d.VolCycleTotal;
    const pos = v => (typeof v==="number" && v>0 ? v : 0);
    const out = {
      ...d,
      CsgBuildup:  buildup!=null ? Math.round(buildup*100)/100 : null,
      BuildupRate: rate!=null ? Math.round(rate*1000)/1000 : null,
      CumVol:      Math.round(cum*100)/100,
      _med: medShut,
      _shutMin:  pos(d.TimeOffTotal),
      _riseMin:  pos(d.ArrivalMinute),
      _afterMin: pos(d.TimeAfterFlow),
      _phase: phase,
      _band:  phase==="ok" ? 0 : 1,
      _bg:    1,
    };
    if (phase==="negative") out._status = `CRITICAL, casing pressure dropped ${Math.abs(out.CsgBuildup)} psi during shut-in (buildup should be positive)`;
    if (phase==="flat")     out._status = "WARNING, casing pressure has flattened during an extended shut-in; extra shut-in is lost flow time";
    return out;
  });
}

/* ── AI hover card: one per plot ─────────────────────────────────────────── */
const ADV_ICON = { "◆":"green", "▲":"amber", "▶":"amber", "■":"red", "▽":"red" };
const ADV_SVG = {
  "▲": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}><path d="M6.5 1.5L11.8 10.5H1.2L6.5 1.5Z" stroke="#f59e0b" strokeWidth="1.4" fill="#f59e0b22" strokeLinejoin="round"/><line x1="6.5" y1="4.5" x2="6.5" y2="7.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round"/><circle cx="6.5" cy="9.2" r="0.8" fill="#f59e0b"/></svg>,
  "■": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}><circle cx="6.5" cy="6.5" r="5" stroke="#ef4444" strokeWidth="1.4" fill="#ef444422"/><path d="M4.5 4.5L8.5 8.5M8.5 4.5L4.5 8.5" stroke="#ef4444" strokeWidth="1.4" strokeLinecap="round"/></svg>,
  "◆": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}><circle cx="6.5" cy="6.5" r="5" stroke="#22c55e" strokeWidth="1.4" fill="#22c55e22"/><path d="M4 6.5L5.8 8.3L9 5" stroke="#22c55e" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  "▽": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}><path d="M6.5 2.5V10M3.5 7L6.5 10L9.5 7" stroke="#f87171" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  "▶": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}><path d="M6.5 10.5V2.5M3.5 5.5L6.5 2.5L9.5 5.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
};

function AdviceTip({ chartId, build, active, payload }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const [pos, setPos] = useState({ x:0, y:0, over:false });
  const prevKey = useRef(null);

  const row  = active && payload?.length ? payload[0]?.payload : null;
  const info = row ? build(row) : null;
  const show = !!info && pos.over;
  const key  = info ? `${info.field}_${Math.round((info.value ?? 0)*10)/10}_${info.cycle ?? ""}` : null;

  useEffect(()=>{
    if (!active) return;
    const onMove = e => setPos({
      x:e.clientX, y:e.clientY,
      over: !!(e.target && e.target.closest && e.target.closest(`[data-pchart="${chartId}"]`)),
    });
    window.addEventListener("mousemove", onMove);
    return ()=>window.removeEventListener("mousemove", onMove);
  },[active, chartId]);

  useEffect(()=>{
    if (!show || !info || info.value==null) return;
    if (prevKey.current === key) return;
    const t = setTimeout(()=>{
      prevKey.current = key;
      const allFields = (info.fields||[]).map(f=>({ ...f, ...(f.field===info.field && info.status ? { status:info.status } : {}) }));
      fetchAdvice(info.field, info.value, info.unit||"", info.cycle ?? null, info.context||{}, allFields);
    }, 350);
    return ()=>clearTimeout(t);
  },[show, key]);

  useEffect(()=>{ if (!active) { prevKey.current = null; clearAdvice(); } },[active]);

  if (!show) return null;

  const sym   = advice ? advice.trim().charAt(0) : "";
  const tone  = S[ADV_ICON[sym]] || S.cyan;
  const body  = advice && ADV_ICON[sym] ? advice.trim().slice(1).trim() : advice;
  const W = 330;
  const est = 64 + info.lines.length*20 + (body ? Math.ceil(body.length/44)*16 + 30 : 44);
  const vw = window.innerWidth, vh = window.innerHeight;
  const left = pos.x + 18 + W > vw ? Math.max(8, pos.x - W - 18) : pos.x + 18;
  const top  = Math.max(8, Math.min(pos.y - 10, vh - est - 12));

  return createPortal(
    <div style={{ position:"fixed", left, top, width:W, zIndex:999999, pointerEvents:"none",
      background:C.card, border:`1px solid ${C.border2}`, borderLeft:`3px solid ${advice?tone:C.border2}`, borderRadius:12,
      padding:"12px 14px", fontFamily:MONO, fontSize:11, boxShadow:"0 14px 40px rgba(0,0,0,0.55)" }}>
      <div style={{ borderBottom:`1px solid ${C.border}`, paddingBottom:7, marginBottom:8 }}>
        <div style={{ color:S.cyan, fontWeight:700, fontSize:12 }}>{info.title}</div>
        {info.sub && <div style={{ color:C.muted, fontSize:9, marginTop:2 }}>{info.sub}</div>}
      </div>
      {info.lines.map((l,i)=>(
        <div key={i} style={{ display:"flex", justifyContent:"space-between", gap:12, marginBottom:4 }}>
          <span style={{ color:C.muted, fontSize:10 }}>{l.label}</span>
          <span>
            <span style={{ color:l.color||C.text, fontWeight:700, fontSize:12 }}>{l.value}</span>
            {l.unit && <span style={{ color:C.muted, fontSize:9, marginLeft:3 }}>{l.unit}</span>}
          </span>
        </div>
      ))}
      <div style={{ marginTop:8, borderRadius:8, padding:"8px 10px", fontSize:10, lineHeight:1.6,
        background:`${advice?tone:C.muted}12` }}>
        <div style={{ color:C.muted, fontSize:8, letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>AI recommendation</div>
        {loading ? <span style={{ color:C.muted }}>Analysing this point…</span>
          : body ? <span style={{ display:"flex", gap:5, alignItems:"flex-start", lineHeight:1.5, color:C.text }}>
                     {ADV_SVG[sym]}<span>{body}</span>
                   </span>
          : <span style={{ color:C.muted }}>Hover over a data point to view a recommendation.</span>}
      </div>
    </div>,
    document.body
  );
}

/* ── tiny helpers ───────────────────────────────────────────────────────── */
const r1 = v => (typeof v==="number" ? Math.round(v*10)/10 : v);
const r2 = v => (typeof v==="number" ? Math.round(v*100)/100 : v);
const num = (obj, keys) => { const o={}; keys.forEach(k=>{ if (typeof obj[k]==="number") o[k]=r2(obj[k]); }); return o; };
const fld = (field,label,value,unit) => (typeof value==="number" ? { field, label, value:r2(value), unit:unit||"" } : null);
const cycleTitle = row => `Cycle ${row.CycleSerialNumber ?? row.index ?? ""}`;
const stateLine = row => (row._phase && row._phase!=="ok" ? [{ label:"Shut-in state", value:PHASE_LABEL[row._phase], color:phaseColor(row._phase) }] : []);
const fmt = (v,d=1) => (typeof v==="number" && isFinite(v) ? v.toFixed(d) : "-");
const vals = (rows,k) => rows.map(d=>d[k]).filter(v=>typeof v==="number" && isFinite(v));
const sum = a => a.reduce((s,v)=>s+v,0);
const avg = a => (a.length ? sum(a)/a.length : null);
const med = a => { if (!a.length) return null; const s=[...a].sort((x,y)=>x-y); return s[Math.floor(s.length/2)]; };

/* ── production trend detection ─────────────────────────────────────────────
   Smooths the series, then finds sustained rises / falls with a zig-zag pass
   (a reversal only counts once it is large relative to the series range).   */
function detectTrends(rows, key, win = 5, minChange = 0.15) {
  const n = rows.length;
  const raw = rows.map(d => (typeof d[key]==="number" && isFinite(d[key]) ? d[key] : null));
  const half = Math.floor(win/2);
  const sm = raw.map((_,i)=>{
    const w = []; for (let j=Math.max(0,i-half); j<=Math.min(n-1,i+half); j++) if (raw[j]!=null) w.push(raw[j]);
    return w.length ? w.reduce((a,b)=>a+b,0)/w.length : null;
  });
  const ok = sm.filter(v=>v!=null);
  if (ok.length < 4) return [];
  const range = Math.max(...ok) - Math.min(...ok);
  if (!(range > 0)) return [];
  const thr = range*minChange;
  const first = sm.findIndex(v=>v!=null);
  let dir = 0, ext = first; const piv = [];
  for (let i=first+1; i<n; i++) {
    const v = sm[i]; if (v==null) continue;
    if (dir===0) {
      if (v - sm[ext] >= thr)      { piv.push(ext); dir = 1;  ext = i; }
      else if (sm[ext] - v >= thr) { piv.push(ext); dir = -1; ext = i; }
    } else if (dir===1) {
      if (v >= sm[ext]) ext = i;
      else if (sm[ext]-v >= thr) { piv.push(ext); dir = -1; ext = i; }
    } else {
      if (v <= sm[ext]) ext = i;
      else if (v-sm[ext] >= thr) { piv.push(ext); dir = 1; ext = i; }
    }
  }
  if (dir!==0) piv.push(ext);
  const out = [];
  for (let k=0; k<piv.length-1; k++) {
    const a = piv[k], b = piv[k+1];
    if (b - a < 2) continue;
    const delta = sm[b]-sm[a];
    const pct = Math.abs(sm[a])>1e-9 ? delta/Math.abs(sm[a])*100 : null;
    out.push({ x1:rows[a].index, x2:rows[b].index, span:(b-a)/n, dir:delta>=0?"up":"down", delta, pct });
  }
  return out;
}
const trendText = (t, unit) => (t.pct!=null && Math.abs(t.pct)<1000
  ? `${t.dir==="up"?"+":"−"}${Math.abs(t.pct).toFixed(0)}%`
  : `${t.dir==="up"?"+":"−"}${Math.abs(t.delta).toFixed(1)} ${unit||""}`.trim());
const trendAt = (idx, trends) => trends.find(t=>idx>=t.x1 && idx<=t.x2) || null;

/* ── small UI blocks ────────────────────────────────────────────────────── */
function Swatch({ color, dashed, block }) {
  return block
    ? <span style={{ display:"inline-block", width:9, height:9, borderRadius:2, background:color }}/>
    : <span style={{ display:"inline-block", width:16, borderTop:`2px ${dashed?"dashed":"solid"} ${color}` }}/>;
}
function Key({ children }) {
  return <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}>{children}</span>;
}
function RowKey({ children }) {
  return <div style={{ display:"flex", gap:16, flexWrap:"wrap", alignItems:"center", minHeight:16, marginBottom:8, paddingLeft:AXIS_W-10, fontFamily:MONO, fontSize:9, color:C.muted }}>{children}</div>;
}
function StageKeys() {
  return (
    <>
      <Key><Swatch block color={S.amber}/>Shut-in</Key>
      <Key><Swatch block color={S.cyan}/>Plunger rise</Key>
      <Key><Swatch block color={S.green}/>After-flow</Key>
    </>
  );
}

function BandSwatch({ color }) {
  return (
    <span style={{ display:"inline-block", position:"relative", width:12, height:12, borderRadius:2, border:`1px solid ${color}`, overflow:"hidden", flexShrink:0 }}>
      <span style={{ position:"absolute", inset:0, background:color, opacity:BAND_OP }}/>
    </span>
  );
}
function BandLegend({ tintOnly }) {
  const item = (color, name, what) => (
    <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}>
      <BandSwatch color={color}/><b style={{ color:C.text, fontWeight:600 }}>{name}</b><span>{what}</span>
    </span>
  );
  return (
    <div style={{ display:"flex", gap:"6px 18px", flexWrap:"wrap", alignItems:"center", minHeight:BAND_LEG_H-14, marginTop:10, paddingTop:8, paddingLeft:AXIS_W-10, borderTop:`1px dashed ${C.border}`, fontFamily:MONO, fontSize:9, color:C.muted }}>
      {!tintOnly && <span style={{ color:C.text, fontWeight:700 }}>Background columns · share of each cycle's time:</span>}
      {!tintOnly && item(S.amber, "Shut-in", "valve closed, casing builds")}
      {!tintOnly && item(S.cyan, "Plunger rise", "plunger travelling to surface")}
      {!tintOnly && item(S.green, "After-flow", "gas flowing after arrival")}
      <span style={{ color:C.text, fontWeight:700 }}>Whole-column tint:</span>
      {item(S.red, "Negative buildup", "critical")}
      {item(S.purple, "Flattening", "warning")}
    </div>
  );
}

function TrendKeys() {
  return (
    <>
      <Key><Swatch block color={S.green}/>▲ Production increase</Key>
      <Key><Swatch block color={S.red}/>▼ Production decrease</Key>
    </>
  );
}
function TrendList({ trends, unit }) {
  const top = [...trends].sort((a,b)=>a.x1-b.x1);
  return (
    <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginTop:8, paddingLeft:AXIS_W-10, fontFamily:MONO, fontSize:9, color:C.muted }}>
      <span>Detected trends:</span>
      {top.length===0 && <span>no sustained increase or decrease</span>}
      {top.map((t,i)=>{
        const c = t.dir==="up" ? S.green : S.red;
        return <span key={i} style={{ border:`1px solid ${c}55`, background:`${c}14`, color:c, borderRadius:10, padding:"1px 8px", whiteSpace:"nowrap" }}>
          {t.dir==="up"?"▲":"▼"} {cycleTickFmt(t.x1)} → {cycleTickFmt(t.x2)} · {trendText(t,unit)}
        </span>;
      })}
    </div>
  );
}

/* collapsible chart details: overview · methodology (named calculations) · interpretation */
function DetailsPanel({ details }) {
  const lab = (t,c) => <span style={{ color:c, fontWeight:700 }}>{t}: </span>;
  return (
    <div style={{ marginBottom:16, padding:"14px 16px", background:`${S.cyan}0f`, border:`1px solid ${S.cyan}33`, borderRadius:10, fontFamily:MONO, fontSize:10.5, lineHeight:1.75, color:C.text }}>
      {details.overview && <div style={{ marginBottom:8 }}>{lab("Overview",S.cyan)}{details.overview}</div>}
      {details.methods && (
        <div style={{ marginBottom:8 }}>
          {lab("Methodology",S.amber)}
          {details.methods.map((m,i)=>(
            <div key={i} style={{ marginTop:4, padding:"5px 10px", background:C.panel, border:`1px solid ${C.border}`, borderRadius:6 }}>
              <b style={{ color:S.amber }}>{m.name}</b>, {m.desc}
            </div>
          ))}
        </div>
      )}
      {details.interpretation && <div>{lab("Interpretation",S.green)}{details.interpretation}</div>}
    </div>
  );
}

function ChartCard({ title, subtitle, children, height = 260, delay = 0, accentColor, details, right, grow }) {
  const ac = accentColor || S.cyan;
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      background:C.card, border:`1px solid ${C.border}`, borderRadius:14,
      padding:"24px 22px 22px", animation:`fade-up 0.5s ease ${delay}s both`,
      position:"relative", overflow:"visible"
    }}>
      <div style={{ position:"absolute", top:0, left:20, right:20, height:2, background:`linear-gradient(to right, transparent, ${ac}66, transparent)` }}/>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:14, flexWrap:"wrap", marginBottom:details && open ? 12 : 16 }}>
        <div style={{ minWidth:0, flex:1 }}>
          <div style={{ fontFamily:SANS, fontWeight:700, fontSize:14, color:C.text }}>{title}</div>
          {subtitle && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:3, lineHeight:1.6 }}>{subtitle}</div>}
        </div>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          {right}
          {details && (
            <button onClick={()=>setOpen(o=>!o)} title="Chart details" style={{
              background:open?`${S.cyan}22`:"transparent", color:open?S.cyan:C.muted, border:`1px solid ${open?S.cyan+"66":C.border2}`,
              borderRadius:20, padding:"4px 12px", fontFamily:MONO, fontSize:10, cursor:"pointer", display:"flex", alignItems:"center", gap:6,
            }}>
              <span style={{ display:"inline-flex", width:14, height:14, borderRadius:"50%", border:`1px solid currentColor`, alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700 }}>i</span>
              {open ? "Hide details" : "Chart details"}
            </button>
          )}
        </div>
      </div>
      {details && open && <DetailsPanel details={details}/>}
      <div style={grow ? { minHeight:height } : { height }}>{children}</div>
      <PlotSummary inline title={title} subtitle={typeof subtitle==="string"?subtitle:""} accent={ac}>{children}</PlotSummary>
    </div>
  );
}

function Kpi({ label, value, unit, color, sub, tip }) {
  return (
    <div title={tip} style={{ background:C.card, border:`1px solid ${C.border}`, borderTop:`3px solid ${color}`, borderRadius:12, padding:"14px 16px" }}>
      <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, letterSpacing:1, textTransform:"uppercase" }}>{label}</div>
      <div style={{ fontFamily:MONO, fontSize:22, fontWeight:700, color, marginTop:5, lineHeight:1 }}>
        {value}{unit && <span style={{ fontSize:11, color:C.muted, marginLeft:4, fontWeight:400 }}>{unit}</span>}
      </div>
      {sub && <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginTop:6, lineHeight:1.5 }}>{sub}</div>}
    </div>
  );
}

function Seg({ opts, value, onChange }) {
  return (
    <div style={{ display:"inline-flex", gap:4, background:C.panel, border:`1px solid ${C.border}`, borderRadius:8, padding:3 }}>
      {opts.map(o=>{
        const on = o.id===value;
        return (
          <button key={o.id} onClick={()=>onChange(o.id)} style={{
            background:on?`${S.cyan}22`:"transparent", color:on?S.cyan:C.muted, border:`1px solid ${on?S.cyan+"55":"transparent"}`,
            borderRadius:6, padding:"5px 12px", fontFamily:MONO, fontSize:10, cursor:"pointer",
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}

function DataTable({ cols, rows, rowKey, onRow, activeKey, renderExpanded, maxH = 340 }) {
  return (
    <div style={{ maxHeight:maxH, overflow:"auto", border:`1px solid ${C.border}`, borderRadius:10 }}>
      <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:MONO, fontSize:10.5, color:C.text }}>
        <thead>
          <tr>
            {cols.map(c=>(
              <th key={c.k} style={{ position:"sticky", top:0, zIndex:1, background:C.panel, color:C.muted, textAlign:c.align||"left", fontWeight:700, fontSize:9, letterSpacing:1, textTransform:"uppercase", padding:"9px 12px", borderBottom:`1px solid ${C.border2}`, whiteSpace:"nowrap" }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length===0 && <tr><td colSpan={cols.length} style={{ padding:"18px 12px", color:C.muted, textAlign:"center" }}>No data available</td></tr>}
          {rows.map(r=>{
            const k = rowKey(r), on = activeKey!=null && activeKey===k;
            return (
              <Fragment key={k}>
                <tr onClick={onRow?()=>onRow(r):undefined} style={{ cursor:onRow?"pointer":"default", background:on?`${S.cyan}18`:"transparent" }}
                    onMouseEnter={e=>{ if(!on) e.currentTarget.style.background=`${C.muted}14`; }}
                    onMouseLeave={e=>{ if(!on) e.currentTarget.style.background="transparent"; }}>
                  {cols.map(c=>(
                    <td key={c.k} style={{ padding:"8px 12px", borderBottom:`1px solid ${C.border}`, textAlign:c.align||"left", verticalAlign:"top", lineHeight:1.5 }}>
                      {c.render ? c.render(r) : r[c.k]}
                    </td>
                  ))}
                </tr>
                {on && renderExpanded && (
                  <tr>
                    <td colSpan={cols.length} style={{ padding:0, borderBottom:`1px solid ${C.border2}`, background:C.panel }}>
                      {renderExpanded(r)}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* factor block used inside the row dropdown */
function Fact({ label, value, unit, sub, color }) {
  return (
    <div style={{ minWidth:0 }}>
      <div style={{ color:C.muted, fontSize:9, letterSpacing:0.5, textTransform:"uppercase" }}>{label}</div>
      <div style={{ color:color||C.text, fontWeight:700, fontSize:14, marginTop:2 }}>
        {value}{unit && <span style={{ color:C.muted, fontSize:9, fontWeight:400, marginLeft:3 }}>{unit}</span>}
      </div>
      {sub && <div style={{ color:C.muted, fontSize:9, marginTop:2, lineHeight:1.5 }}>{sub}</div>}
    </div>
  );
}

/* row dropdown: cycle factors + AI recommendation (generated, not hard-coded) */
function CycleDetail({ row, prev, medShut }) {
  const { advice, loading, fetchAdvice } = useAIAdvice();
  const tot = (row._shutMin||0)+(row._riseMin||0)+(row._afterMin||0);
  const pct = v => (tot>0 ? `${Math.round((v/tot)*100)}% of cycle` : "-");
  const mult = medShut && typeof row.TimeOffTotal==="number" ? row.TimeOffTotal/medShut : null;
  const csgTbg = typeof row.CsgAtOpen==="number" && typeof row.TbgAtOpen==="number" ? row.CsgAtOpen-row.TbgAtOpen : null;
  const pc = phaseColor(row._phase);

  useEffect(()=>{
    if (typeof row.TimeOffTotal!=="number") return;
    const fields = [
      fld("TimeOffTotal","Shut-in time",row.TimeOffTotal,"min"), fld("CsgBuildup","Casing buildup",row.CsgBuildup,"psi"),
      fld("BuildupRate","Buildup rate",row.BuildupRate,"psi/min"), fld("CsgAtOpen","Casing @ open",row.CsgAtOpen,"psi"),
      fld("TbgAtOpen","Tubing @ open",row.TbgAtOpen,"psi"), fld("LineAtOpen","Line @ open",row.LineAtOpen,"psi"),
      fld("FlowRateAtClose","Flow rate",row.FlowRateAtClose,"MCF/d"), fld("TimeAfterFlow","After-flow time",row.TimeAfterFlow,"min"),
    ].filter(Boolean).map(f=>(f.field==="TimeOffTotal" && row._status ? { ...f, status:row._status } : f));
    fetchAdvice("TimeOffTotal", row.TimeOffTotal, "min", row.index,
      { chart:"Cycle detail", shut_in_vs_median:mult!=null?r2(mult):null, ...num(row,["TimeOffTotal","CsgBuildup","BuildupRate","CsgAtOpen","CsgAtClose","TbgAtOpen","LineAtOpen","FlowRateAtClose","ArrivalMinute","TimeAfterFlow"]) },
      fields);
  },[row.index]);

  const sym  = advice ? advice.trim().charAt(0) : "";
  const tone = S[ADV_ICON[sym]] || S.cyan;
  const body = advice && ADV_ICON[sym] ? advice.trim().slice(1).trim() : advice;
  const grid = { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:14 };
  const head = t => <div style={{ color:C.text, fontFamily:SANS, fontWeight:700, fontSize:11, margin:"0 0 8px" }}>{t}</div>;

  return (
    <div style={{ padding:"14px 18px 16px", fontFamily:MONO, fontSize:10.5, lineHeight:1.6, borderLeft:`3px solid ${pc}`, cursor:"default" }} onClick={e=>e.stopPropagation()}>
      <div style={{ color:C.muted, fontSize:10, marginBottom:12 }}>
        {cycleTitle(row)} · {row.time} · <span style={{ color:pc, fontWeight:700 }}>{PHASE_LABEL[row._phase]}</span>
      </div>

      {head("Shut-in factors")}
      <div style={{ ...grid, marginBottom:14 }}>
        <Fact label="Shut-in duration" value={fmt(row.TimeOffTotal)} unit="min" sub={mult!=null?`${fmt(mult,1)}× the typical (median) shut-in of ${fmt(medShut,1)} min`:undefined}/>
        <Fact label="Casing buildup" value={fmt(row.CsgBuildup,2)} unit="psi" color={row._phase==="ok"?undefined:pc} sub={`Casing at open ${fmt(row.CsgAtOpen)} psi vs previous cycle's casing at close ${fmt(prev?.CsgAtClose)} psi`}/>
        <Fact label="Buildup rate" value={fmt(row.BuildupRate,3)} unit="psi/min" sub="Casing pressure gained per minute of shut-in"/>
      </div>

      {head("Cycle stages")}
      <div style={{ ...grid, marginBottom:14 }}>
        <Fact label="Shut-in" value={fmt(row._shutMin)} unit="min" color={S.amber} sub={pct(row._shutMin)}/>
        <Fact label="Plunger rise" value={fmt(row._riseMin)} unit="min" color={S.cyan} sub={pct(row._riseMin)}/>
        <Fact label="After-flow" value={fmt(row._afterMin)} unit="min" color={S.green} sub={pct(row._afterMin)}/>
      </div>

      {head("Pressure & production")}
      <div style={{ ...grid, marginBottom:14 }}>
        <Fact label="Casing @ open" value={fmt(row.CsgAtOpen)} unit="psi" color={S.cyan}/>
        <Fact label="Tubing @ open" value={fmt(row.TbgAtOpen)} unit="psi" color={S.teal}/>
        <Fact label="Line @ open" value={fmt(row.LineAtOpen)} unit="psi" color={S.blue}/>
        <Fact label="Casing, tubing differential" value={fmt(csgTbg)} unit="psi" sub="Pressure available to lift the plunger"/>
        <Fact label="Flow rate" value={fmt(row.FlowRateAtClose)} unit="MCF/d" color={S.green}/>
        <Fact label="Cycle volume" value={fmt(row.VolCycleTotal,2)} unit="MCF" color={S.green}/>
      </div>

      {row._status && <div style={{ color:pc, marginBottom:12 }}>{row._status}</div>}

      <div style={{ borderRadius:8, padding:"8px 10px", fontSize:10, lineHeight:1.6, background:`${advice?tone:C.muted}12` }}>
        <div style={{ color:C.muted, fontSize:8, letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>AI recommendation</div>
        {loading ? <span style={{ color:C.muted }}>Analysing this cycle…</span>
          : body ? <span style={{ display:"flex", gap:5, alignItems:"flex-start", lineHeight:1.5, color:C.text }}>{ADV_SVG[sym]}<span>{body}</span></span>
          : <span style={{ color:C.muted }}>No recommendation available.</span>}
      </div>
    </div>
  );
}

/* ── chart details (opened with "Chart details") ────────────────────────── */
const CHART_DETAILS = {
  hero: {
    overview: "Casing, tubing and line pressure at valve open for each cycle, plotted over the cycle-stage profile. The background column for each cycle shows how its time was divided between shut-in, plunger rise and after-flow.",
    methods: [
      { name:"Cycle Stage Distribution", desc:"Share of each plunger cycle spent in shut-in, plunger rise and after-flow." },
      { name:"Casing: Tubing Differential", desc:"Casing minus tubing pressure at valve open; the pressure available to lift the plunger and liquid slug." },
    ],
    interpretation: "Casing should build through shut-in and decline once the valve opens, while tubing remains below casing. A tinted column marks a cycle flagged by a shut-in rule.",
  },
  flow: {
    overview: "Gas flow rate at valve close (MCF/d) for each cycle, shown over the same cycle-stage profile as the pressure chart.",
    methods: [
      { name:"Flow Rate at Close", desc:"Gas rate recorded when the valve closes at the end of the cycle." },
      { name:"Production Trend Detection", desc:"Flow is smoothed over a few cycles and sustained rises and falls are marked in the ribbon at the top of the plot, with the percentage change over each period." },
    ],
    interpretation: "Flow should be low during shut-in and increase through plunger rise and after-flow. Flow recorded during shut-in suggests a data or valve-state issue.",
  },
  cum: {
    overview: "Total gas produced to date, accumulated cycle by cycle across the cycles shown.",
    methods: [
      { name:"Cumulative Gas Produced", desc:"Running total of the gas volume produced in each cycle (MCF)." },
      { name:"Production Trend Detection", desc:"Applied to per-cycle volume, so the ribbon at the top shows where production per cycle rises or falls (steeper or flatter cumulative slope)." },
    ],
    interpretation: "The slope represents production per cycle. A flatter slope indicates cycles with low output.",
  },
  stage: {
    overview: "Share of each cycle spent in shut-in, plunger rise and after-flow.",
    methods: [
      { name:"Cycle Stage Distribution", desc:"Percentage of total cycle time spent in shut-in (casing builds pressure), plunger rise (plunger and liquid slug travel to surface) and after-flow (gas production after plunger arrival)." },
    ],
    interpretation: "A growing shut-in share indicates the well is held shut for longer each cycle. A dominant after-flow share is often driven by a few very long after-flow periods.",
  },
  buildup: {
    overview: "Casing pressure gained during shut-in for each cycle.",
    methods: [
      { name:"Casing Buildup", desc:"Casing pressure gained during shut-in: casing at open minus casing at close of the previous cycle." },
      { name:"Negative Buildup Alert (Critical)", desc:"Cycles in which casing lost pressure while the well was shut in." },
      { name:"Evaluation Basis", desc:"Buildup is evaluated between consecutive cycles. Analysis within a single shut-in requires continuous pressure data aligned to cycle times." },
    ],
    interpretation: "Buildup should be positive. Red bars are critical cycles; amber bars are flattening cycles.",
  },
  rate: {
    overview: "Casing pressure buildup per minute of shut-in.",
    methods: [
      { name:"Buildup Rate", desc:"Casing pressure gained per minute of shut-in." },
      { name:"Flattening Alert (Warning)", desc:"Long shut-in with a minimal buildup rate; extra shut-in adds little pressure and costs flow time." },
    ],
    interpretation: "Healthy cycles remain above the flat-rate line. Points below it during long shut-ins are flagged as flattening.",
  },
  shut: {
    overview: "Total shut-in minutes per cycle compared with the typical shut-in.",
    methods: [
      { name:"Typical Shut-in (Median)", desc:"Middle value of shut-in duration across all cycles in the window. It is used as the baseline so a few very long shut-ins do not distort it." },
      { name:"Long Shut-in Limit", desc:"Shut-in duration above a set multiple of the typical shut-in; classed as extended." },
      { name:"Lost Shut-in Time", desc:"Shut-in minutes beyond the typical duration across flattening cycles; flow time that could be recovered." },
    ],
    interpretation: "Cycles above the limit line are extended shut-ins. Those that also fall below the flat-rate line are flagged as flattening.",
  },
  press: {
    overview: "Selected pressure at valve open and valve close, shown over the cycle-stage profile.",
    methods: [
      { name:"Open-to-Close Drop", desc:"Pressure decline from valve open to valve close within a cycle." },
      { name:"Pressure Differential", desc:"Mean pressure difference at valve open: casing, tubing (lift driving force), tubing, line (drawdown against the sales line) and casing, line." },
    ],
    interpretation: "Casing should peak at open and decline by close. Tubing should fall sharply after opening. Line pressure is the sales-line back-pressure and should remain relatively stable.",
  },
  hist: {
    overview: "Number of cycles opening in each pressure range for casing, tubing and line.",
    methods: [
      { name:"Share of Cycles", desc:"Cycles in a pressure range as a percentage of all cycles." },
    ],
    interpretation: "A narrow peak indicates consistent operation. A wide or two-peaked shape indicates the well opens at inconsistent pressures, which can point to time-based rather than pressure-based cycling.",
  },
};

const ROWS = [
  { id:"csg",  title:"Casing Pressure", open:"CsgAtOpen",  close:"CsgAtClose",  oc:"cyan",   cc:"purple",   desc:"Builds during shut-in, drops through plunger rise and after-flow" },
  { id:"tbg",  title:"Tubing Pressure", open:"TbgAtOpen",  close:"TbgAtClose",  oc:"teal",   cc:"darkCyan", desc:"Should fall sharply once the valve opens" },
  { id:"line", title:"Line Pressure",   open:"LineAtOpen", close:"LineAtClose", oc:"blue",   cc:"pink",     desc:"Sales-line back-pressure the well flows against" },
];
const HIST = [
  { id:"csg",  title:"Casing", open:"CsgAtOpen",  key:"Casing", accent:"cyan"   },
  { id:"tbg",  title:"Tubing", open:"TbgAtOpen",  key:"Tubing", accent:"teal"   },
  { id:"line", title:"Line",   open:"LineAtOpen", key:"Line",   accent:"blue"   },
];

const PSUBS = [
  { id:"timeline", label:"Cycle timeline",    sub:"Pressure, flow, cycle stages" },
  { id:"stages",   label:"Cycle stages",      sub:"Shut-in, rise, after-flow" },
  { id:"health",   label:"Shut-in health",    sub:"Diagnostics, alerts" },
  { id:"profiles", label:"Pressure profiles", sub:"Open vs close, distributions" },
];

/* ══════════════════════════════════════════════════════════════════════════
   VELOCITY TAB, exact VelocityPage content
   ══════════════════════════════════════════════════════════════════════════ */
const LEGEND_PROPS = {
  wrapperStyle: { fontFamily: MONO, fontSize: 10, paddingBottom: 4 },
  verticalAlign: "top", align: "right", iconSize: 8,
};

function AnimNum({ to, dec = 1, suffix = "" }) {
  if (to == null || isNaN(Number(to))) return <span>, </span>;
  return <span>{Number(to).toFixed(dec)}{suffix}</span>;
}

/* ───────────── TIME-WINDOW HELPERS (same bar / axis / look as Overview, AI Controller, GLR) ───────────── */
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const pad2 = n => String(n).padStart(2, "0");
const HOUR = 3600000, DAY = 86400000;

// backend sends "YYYY-MM-DD HH:MM" - parse as plain local clock time (no timezone shifting)
function parseT(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime() : null;
}
const fmtDT   = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const fmtD    = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`; };
const fmtHM   = ms => { const d = new Date(ms); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const toInput = ms => { const d = new Date(ms); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };

function fmtSpan(ms) {
  if (ms < HOUR)      return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (ms < 2 * DAY)   return `${Math.round(ms / HOUR)} h`;
  const d = Math.round(ms / DAY);
  return d >= 14 ? `${d} days (${(d / 7).toFixed(1)} wk)` : `${d} days`;
}
function fmtAgo(ms) {
  if (ms < HOUR)    return "just now";
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)} h ago`;
  return `${Math.round(ms / DAY)} d ago`;
}

const PRESETS = [
  { id: "24h",   label: "24 H",   ms: DAY },
  { id: "7d",    label: "7 D",    ms: 7 * DAY },
  { id: "30d",   label: "30 D",   ms: 30 * DAY },
  { id: "90d",   label: "90 D",   ms: 90 * DAY },
  { id: "cyc30", label: "LAST 30 CYC", cycles: 30 },
  { id: "all",   label: "ALL" },
];
const MIN_POINTS = 3;   // fewer cycles than this cannot draw a meaningful trend

/* Apply a window to ANY array of rows that carry `_t` (ms). Presets are measured back from the LATEST cycle in the data, not from "now". */
function applyWin(w, tMax, arr) {
  if (w.mode === "all" || tMax == null) return arr;
  if (w.mode === "cyc30") {
    if (arr.some(r => r.well != null)) {            // multi-well rows -> newest 30 of EACH well
      const by = {};
      arr.forEach(r => { (by[r.well] = by[r.well] || []).push(r); });
      return Object.values(by).flatMap(a => a.slice(-30));
    }
    return arr.slice(-30);
  }
  if (w.mode === "custom") {
    const f = w.from ? parseT(`${w.from} 00:00`) : -Infinity;
    const t = w.to   ? parseT(`${w.to} 23:59`)   :  Infinity;
    return arr.filter(d => d._t != null && d._t >= f && d._t <= t);
  }
  const p = PRESETS.find(x => x.id === w.mode);
  return arr.filter(d => d._t != null && d._t >= tMax - p.ms);
}
/* `rows` = every cycle time the window bar should know about ({_t, well?}). `filter(arr)` windows any per-well / per-view array the same way. */
function useTimeWindow(rows) {
  const [win, setWin] = useState({ mode: "all", from: "", to: "" });
  const tMin = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.min(...v) : null; }, [rows]);
  const tMax = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.max(...v) : null; }, [rows]);
  const view = useMemo(() => applyWin(win, tMax, rows), [rows, win, tMax]);
  const presetCounts = useMemo(() => Object.fromEntries(PRESETS.map(p => [p.id, applyWin({ mode: p.id }, tMax, rows).length])), [rows, tMax]);
  return { win, setWin, view, presetCounts, tMin, tMax, isAll: win.mode === "all", filter: arr => applyWin(win, tMax, arr) };
}

/* Second row of the X axis = timestamp of the cycle sitting at that position. */
function DateTick({ x, y, payload, index, lookup, lastIdx, shortSpan }) {
  const t = lookup.get(payload.value);
  if (t == null) return null;
  const anchor = index === 0 ? "start" : index === lastIdx ? "end" : "middle";
  const l1 = shortSpan ? fmtHM(t) : fmtD(t);
  const l2 = shortSpan ? fmtD(t)  : fmtHM(t);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor={anchor} fill={C.amber} fontSize={9} fontFamily={MONO} dy={11}>{l1}</text>
      <text textAnchor={anchor} fill={C.muted} fontSize={8} fontFamily={MONO} dy={22}>{l2}</text>
    </g>
  );
}

/* Call as a function (NOT <Component/>) - recharts only recognises <XAxis> as a direct child.
   Rows must carry `key` (default "index") and `_t`. labelText = the "Cycle Number →" caption, moved under the timestamp row. */
function dateXAxis(rows, key = "index", nTicks = 5, labelText, opts = {}) {
  const stamped = rows.filter(r => r._t != null);
  if (!stamped.length) return null;
  const last = stamped.length - 1;
  const idx = new Set();
  for (let i = 0; i < nTicks; i++) idx.add(Math.round((i * last) / Math.max(nTicks - 1, 1)));
  const ticks = [...idx].filter(i => stamped[i]).map(i => stamped[i][key]);
  const lookup = new Map(stamped.map(r => [r[key], r._t]));
  const span = stamped.length > 1 ? (stamped[last]._t - stamped[0]._t) : 0;
  return (
    <XAxis xAxisId="date" dataKey={key} ticks={ticks} interval={0} tickLine={false} axisLine={false} height={opts.height || 32}
      tick={<DateTick lookup={lookup} lastIdx={ticks.length - 1} shortSpan={span > 0 && span < 3 * DAY} />}
      label={labelText ? { value: labelText, position: "insideBottom", offset: opts.labelOffset ?? -14, fill: C.text, fontSize: 11, fontFamily: MONO } : undefined} />
  );
}
const hasStamps = rows => rows.some(r => r._t != null);

function DataWindowBar({ ctl, total, note, isDark }) {
  const { win, setWin, view, presetCounts, tMin, tMax, isAll } = ctl;
  const vs      = view.filter(d => d._t != null);
  const vStart  = vs.length ? Math.min(...vs.map(d => d._t)) : null;
  const vEnd    = vs.length ? Math.max(...vs.map(d => d._t)) : null;
  const hasTime = vStart != null && vEnd != null;
  const spanMs  = hasTime ? Math.max(vEnd - vStart, 0) : 0;
  const tooFew  = view.length < MIN_POINTS;
  const latestAgo = tMax != null ? Date.now() - tMax : null;
  const btn = (active, disabled) => ({
    fontFamily: MONO, fontSize: 10, letterSpacing: 1, padding: "5px 11px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
    background: active ? `${C.amber}22` : "transparent", color: disabled ? C.muted2 : active ? C.amber : C.muted,
    border: `1px solid ${active ? C.amber : C.border2}`, opacity: disabled ? 0.55 : 1, whiteSpace: "nowrap",
  });
  const dateInput = { fontFamily: MONO, fontSize: 10, background: C.panel, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "4px 8px", colorScheme: isDark ? "dark" : "light" };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 12, padding: "14px 18px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: C.muted }}>DATA WINDOW</span>
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.text }}>
          {hasTime ? `${fmtDT(vStart)}  →  ${fmtDT(vEnd)}` : "no timestamps available"}
        </span>
        {hasTime && <span style={{ fontFamily: MONO, fontSize: 10, color: C.amber, background: `${C.amber}14`, border: `1px solid ${C.amber}33`, borderRadius: 5, padding: "2px 8px" }}>
          {isAll ? "CUMULATIVE" : "WINDOW"} · {fmtSpan(spanMs)}
        </span>}
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.cyan, background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`, borderRadius: 5, padding: "2px 8px" }}>
          {view.length} of {total} cycles
        </span>
        {latestAgo != null && (
          <span title="Presets are measured back from the newest cycle in the data, not from today's date"
            style={{ fontFamily: MONO, fontSize: 10, color: latestAgo > DAY ? C.orange : C.green, background: `${latestAgo > DAY ? C.orange : C.green}12`, border: `1px solid ${latestAgo > DAY ? C.orange : C.green}33`, borderRadius: 5, padding: "2px 8px" }}>
            Latest cycle {fmtDT(tMax)} · {fmtAgo(latestAgo)}
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {PRESETS.map(p => {
          const n = presetCounts[p.id];
          const disabled = p.id !== "all" && n < MIN_POINTS;
          return (
            <button key={p.id} disabled={disabled} onClick={() => setWin({ mode: p.id, from: "", to: "" })}
              title={disabled ? `Only ${n} cycle${n === 1 ? "" : "s"} in this window, too few to plot a trend. Try a wider window.` : `${n} cycles`}
              style={btn(win.mode === p.id, disabled)}>
              {p.label} <span style={{ opacity: 0.7 }}>· {n}</span>
            </button>
          );
        })}
        <button onClick={() => setWin(w => ({ mode: "custom", from: w.from || (tMin != null ? toInput(tMin) : ""), to: w.to || (tMax != null ? toInput(tMax) : "") }))}
          style={btn(win.mode === "custom", false)}>CUSTOM RANGE</button>
        {win.mode === "custom" && (
          <>
            <input type="date" value={win.from} min={tMin != null ? toInput(tMin) : undefined} max={win.to || (tMax != null ? toInput(tMax) : undefined)}
              onChange={e => setWin(w => ({ ...w, from: e.target.value }))} style={dateInput} />
            <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>to</span>
            <input type="date" value={win.to} min={win.from || (tMin != null ? toInput(tMin) : undefined)} max={tMax != null ? toInput(tMax) : undefined}
              onChange={e => setWin(w => ({ ...w, to: e.target.value }))} style={dateInput} />
          </>
        )}
      </div>
      {tooFew && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.orange, marginTop: 8 }}>
          ⚠ Only {view.length} cycle{view.length === 1 ? "" : "s"} in this range, widen the window to see a trend.
        </div>
      )}
    </div>
  );
}


/* window-aware versions of the backend histograms / stats (only used when the window is NOT "ALL") */
function velStatsOf(rows) {
  const v = vals(rows, "ArrivalVelocity");
  if (!v.length) return null;
  const n = v.length, mean = sum(v) / n, s = [...v].sort((a, b) => a - b);
  const median = n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
  const std = n > 1 ? Math.sqrt(v.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1)) : 0;
  const lo = s[0], hi = s[n - 1], N = 25, W = (hi > lo ? hi - lo : 1) / N;     // same 25 equal-width bins as the backend
  const counts = new Array(N).fill(0);
  v.forEach(x => { let k = Math.floor((x - lo) / W); if (k >= N) k = N - 1; counts[k]++; });
  return { bins: Array.from({ length: N }, (_, k) => +(lo + k * W).toFixed(2)), counts,
           mean: +mean.toFixed(2), median: +median.toFixed(2), std: +std.toFixed(2), min: +lo.toFixed(2), max: +hi.toFixed(2) };
}
function pressDistOf(rows) {
  const cols = ["CsgAtOpen", "TbgAtOpen", "LineAtOpen", "CsgAtClose", "TbgAtClose", "LineAtClose"];
  const series = {};
  cols.forEach(c => { const v = vals(rows, c); if (v.length) series[c] = v; });
  const all = Object.values(series).flat();
  if (!all.length || !series.CsgAtOpen) return [];
  let lo = Math.min(...all), hi = Math.max(...all);
  if (hi <= lo) hi = lo + 1;
  const N = 30, W = (hi - lo) / N;                                              // one shared set of 30 bin edges, like the backend
  const cnt = c => { const a = new Array(N).fill(0); (series[c] || []).forEach(x => { let k = Math.floor((x - lo) / W); if (k >= N) k = N - 1; a[k]++; }); return a; };
  const cs = cnt("CsgAtOpen"), ts = cnt("TbgAtOpen"), ls = cnt("LineAtOpen");
  return Array.from({ length: N }, (_, k) => ({ bin: (lo + k * W).toFixed(0), Casing: cs[k], Tubing: ts[k], Line: ls[k], _actualFieldKey: "CsgAtOpen" }));
}

function VelocityTab({ tsData, tsCols, arrStats, histData, TTip }) {
  const hasT = hasStamps(tsData || []);
  return (
    <>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:18, marginBottom:18 }}>
        <ChartCard title="Arrival Velocity Distribution" subtitle="Frequency by velocity range" accentColor={C.amber} height={300} delay={0.05}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={histData} margin={CHART_MARGIN_HISTO}>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="bin" {...CYCLE_XAXIS_PROPS()} label={{ value:"Velocity (ft/min)", position:"insideBottom", offset:-34, fill:C.text, fontSize:11, fontFamily:MONO }}/>
              <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={{ value:"Cycles", angle:-90, position:"insideLeft", offset:0, fill:C.text, fontSize:11, fontFamily:MONO }}/>
              <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
              {arrStats && <ReferenceLine x={arrStats.mean.toFixed(0)} stroke={C.pink} strokeDasharray="4 2" label={{ value:`μ ${arrStats.mean.toFixed(1)}`, fill:C.pink, fontSize:10, fontFamily:MONO, position:"top" }}/>}
              <Bar dataKey="count" name="Frequency" fill={C.amber} fillOpacity={0.85} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"22px 20px", animation:"fade-up 0.5s ease 0.1s both" }}>
          <div style={{ fontFamily:SANS, fontWeight:700, fontSize:14, marginBottom:18 }}>Arrival Velocity Statistics</div>
          {arrStats && [
            ["Mean",   arrStats.mean,   C.cyan],
            ["Median", arrStats.median, C.teal],
            ["Std Dev",arrStats.std,    C.purple],
            ["Min",    arrStats.min,    C.green],
            ["Max",    arrStats.max,    C.red],
          ].map(([label,val,color])=>(
            <div key={label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontFamily:MONO, fontSize:12, color:C.muted }}>{label}</span>
              <div style={{ fontFamily:MONO, fontSize:20, fontWeight:700, color }}>
                <AnimNum to={val} dec={2} suffix=" ft/min"/>
              </div>
            </div>
          ))}
        </div>
      </div>

      <ChartCard title="Arrival Velocity: Trend Over All Cycles" subtitle="ft/min per cycle with mean reference" accentColor={C.amber} height={280} delay={0.15}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={tsData} margin={{ ...CHART_MARGIN_DEFAULT, left: 8 }}>
            <defs>
              <linearGradient id="gveltrend" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={C.amber} stopOpacity={0.4}/><stop offset="95%" stopColor={C.amber} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
            <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()} height={hasT ? 18 : undefined} label={hasT ? undefined : { value:"Cycle", position:"insideBottom", offset:-34, fill:C.text, fontSize:11, fontFamily:MONO }}/>
            {dateXAxis(tsData || [], "index", 5, "Cycle index", { height:70, labelOffset:2 })}
            <YAxis width={AXIS_W} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={yLbl("Velocity (ft/min)", C.muted)}/>
            <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
            <Legend {...LEGEND_PROPS}/>
            {arrStats && <ReferenceLine y={arrStats.mean} stroke={C.amber+"88"} strokeDasharray="6 3"/>}
            <Area type="monotone" dataKey="ArrivalVelocity" name="Velocity (ft/min)" stroke={C.amber} fill="url(#gveltrend)" strokeWidth={2} dot={false}/>
            {tsCols.includes("VentMin") && <Line type="monotone" dataKey="VentMin" name="Vent Min" stroke={C.purple} strokeWidth={1.5} dot={false} strokeDasharray="3 3"/>}
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   PRESSURE TAB, four sub-tabs
   ══════════════════════════════════════════════════════════════════════════ */
function PressureTab({ tsData, tsCols, pressData }) {
  const [sub, setSub] = useState("timeline");
  const rules = DEFAULT_RULES;
  const [flowMode, setFlowMode] = useState("stacked");
  const [pk, setPk] = useState("csg");
  const [sel, setSel] = useState(null);

  const ctx = useMemo(()=>buildContext(tsData||[], rules),[tsData]);
  const medShut = ctx[0]?._med ?? null;
  const flowTrends = useMemo(()=>detectTrends(ctx,"FlowRateAtClose"),[ctx]);
  const volTrends  = useMemo(()=>detectTrends(ctx,"VolCycleTotal"),[ctx]);
  const tickEvery = Math.max(0, Math.ceil(ctx.length/16)-1);
  const n = k => ctx.filter(d=>d._phase===k).length;
  const flagged = ctx.filter(d=>d._phase!=="ok");
  const cursor = { stroke:C.muted, strokeWidth:1 };

  const lostMin = sum(ctx.filter(d=>d._phase==="flat").map(d=>Math.max(0, d.TimeOffTotal - (medShut||0))));
  const totalTime = sum(ctx.map(d=>d._shutMin+d._riseMin+d._afterMin));

  /* shared axes (plain elements: recharts only accepts its own components as direct children).
     X axis shows cycle numbers only, timestamps are shown in the hover card. */
  const gridEl = <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>;
  const hasT = hasStamps(ctx);
  /* two-row X axis (same as Overview): row 1 = cycle #, row 2 = start time of that cycle (second axis, no caption to keep plot height) */
  const xAxisEl = <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()} interval={tickEvery} height={hasT ? 18 : XAXIS_H} tickMargin={4}
    label={hasT ? undefined : { value:"Cycle", position:"insideBottom", offset:0, fill:C.text, fontSize:11, fontFamily:MONO }}/>;
  const dateAxisEl = dateXAxis(ctx, "index", 5, "Cycle index", { height:70, labelOffset:2 });
  const bandAxis = <YAxis yAxisId="band" hide domain={[0,1]}/>;
  const bandBar  = <Bar yAxisId="band" dataKey="_bg" isAnimationActive={false} legendType="none" shape={<StageBand/>}/>;
  const flagBar  = (
    <Bar yAxisId="band" dataKey="_band" isAnimationActive={false} legendType="none">
      {ctx.map((d,i)=><Cell key={i} fill={phaseColor(d._phase)} fillOpacity={0.14}/>)}
    </Bar>
  );
  const flowGrad = id => (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="5%" stopColor={S.green} stopOpacity={0.25}/><stop offset="95%" stopColor={S.green} stopOpacity={0.03}/>
      </linearGradient>
    </defs>
  );

  const trendAreas = trends => trends.map((t,i)=>{
    const c = t.dir==="up" ? S.green : S.red;
    return <ReferenceArea key={`tr${i}`} yAxisId="band" x1={t.x1} x2={t.x2} y1={0.9} y2={1} fill={c} fillOpacity={0.45} stroke={c} strokeOpacity={0.8} ifOverflow="hidden"
      label={t.span>=0.07 ? { value:`${t.dir==="up"?"▲":"▼"} ${trendText(t)}`, position:"insideTop", fill:C.text, fontSize:9, fontFamily:MONO } : undefined}/>;
  });

  const binW = useMemo(()=>{
    const a = Number(pressData?.[0]?.bin), b = Number(pressData?.[1]?.bin);
    return isNaN(a)||isNaN(b) ? 0 : b-a;
  },[pressData]);

  const histRows = useMemo(()=>HIST.map(def=>{
    const data = (pressData||[]).map(p=>({ bin:p.bin, count:p[def.key]||0 }));
    return { def, data, total:data.reduce((s,d)=>s+d.count,0) };
  }),[pressData]);

  /* ── tip builders (timestamp is shown as the hover-card subtitle) ── */
  const tipHero = row => ({
    title:cycleTitle(row), sub:row.time, field:"CsgAtOpen", value:row.CsgAtOpen, unit:"psi", cycle:row.index,
    lines:[
      { label:"Casing @ open", value:r1(row.CsgAtOpen), unit:"psi", color:S.cyan },
      { label:"Tubing @ open", value:r1(row.TbgAtOpen), unit:"psi", color:S.teal },
      { label:"Line @ open", value:r1(row.LineAtOpen), unit:"psi", color:S.blue },
      { label:"Flow rate", value:r1(row.FlowRateAtClose), unit:"MCF/d", color:S.green },
      { label:"Shut-in", value:r1(row._shutMin), unit:"min", color:S.amber },
      { label:"Plunger rise", value:r1(row._riseMin), unit:"min", color:S.cyan },
      { label:"After-flow", value:r1(row._afterMin), unit:"min", color:S.green },
      ...stateLine(row),
    ],
    context:{ chart:"Cycle timeline", ...num(row,["CsgAtOpen","TbgAtOpen","LineAtOpen","FlowRateAtClose","TimeOffTotal","ArrivalMinute","TimeAfterFlow","CsgBuildup"]) },
    fields:[fld("CsgAtOpen","Casing @ open",row.CsgAtOpen,"psi"), fld("TbgAtOpen","Tubing @ open",row.TbgAtOpen,"psi"),
            fld("LineAtOpen","Line @ open",row.LineAtOpen,"psi"), fld("FlowRateAtClose","Flow rate",row.FlowRateAtClose,"MCF/d"),
            fld("TimeOffTotal","Shut-in time",row.TimeOffTotal,"min"), fld("CsgBuildup","Casing buildup",row.CsgBuildup,"psi")].filter(Boolean),
    status:row._status,
  });
  const tipProd = (trends, unit) => row => ({
    title:cycleTitle(row), sub:row.time, field:"FlowRateAtClose", value:row.FlowRateAtClose, unit:"MCF/d", cycle:row.index,
    lines:[
      { label:"Flow rate", value:r1(row.FlowRateAtClose), unit:"MCF/d", color:S.green },
      { label:"Cycle volume", value:r2(row.VolCycleTotal), unit:"MCF", color:S.green },
      { label:"Running total", value:r1(row.CumVol), unit:"MCF", color:S.amber },
      ...(trendAt(row.index,trends) ? [{ label:"Production trend", value:`${trendAt(row.index,trends).dir==="up"?"▲ Increase":"▼ Decrease"} ${trendText(trendAt(row.index,trends),unit)}`, color:trendAt(row.index,trends).dir==="up"?S.green:S.red }] : []),
      ...stateLine(row),
    ],
    context:{ chart:"Production", ...(trendAt(row.index,trends) ? { production_trend:`${trendAt(row.index,trends).dir==="up"?"increasing":"decreasing"} ${trendText(trendAt(row.index,trends),unit)}` } : {}), ...num(row,["FlowRateAtClose","VolCycleTotal","TimeOffTotal","TimeAfterFlow","CsgBuildup"]) },
    fields:[fld("FlowRateAtClose","Flow Rate At Close",row.FlowRateAtClose,"MCF/d"), fld("VolCycleTotal","Cycle Volume",row.VolCycleTotal,"MCF"),
            fld("TimeOffTotal","Shut-in time",row.TimeOffTotal,"min"), fld("TimeAfterFlow","After-flow time",row.TimeAfterFlow,"min")].filter(Boolean),
    status:row._status,
  });
  const tipStage = row => {
    const tot = (row._shutMin||0)+(row._riseMin||0)+(row._afterMin||0);
    const pct = v => (tot>0 ? `${Math.round((v/tot)*100)}%` : "-");
    return {
      title:cycleTitle(row), sub:row.time, field:"TimeOffTotal", value:row.TimeOffTotal, unit:"min", cycle:row.index,
      lines:[
        { label:"Shut-in", value:`${r1(row._shutMin)} min · ${pct(row._shutMin)}`, color:S.amber },
        { label:"Plunger rise", value:`${r1(row._riseMin)} min · ${pct(row._riseMin)}`, color:S.cyan },
        { label:"After-flow", value:`${r1(row._afterMin)} min · ${pct(row._afterMin)}`, color:S.green },
        ...stateLine(row),
      ],
      context:{ chart:"Cycle stages", ...num(row,["TimeOffTotal","ArrivalMinute","TimeAfterFlow","CsgBuildup","BuildupRate"]) },
      fields:[fld("TimeOffTotal","Shut-in time",row.TimeOffTotal,"min"), fld("ArrivalMinute","Plunger rise time",row.ArrivalMinute,"min"),
              fld("TimeAfterFlow","After-flow time",row.TimeAfterFlow,"min"), fld("CsgBuildup","Casing buildup",row.CsgBuildup,"psi")].filter(Boolean),
      status:row._status,
    };
  };
  const tipPress = def => row => {
    const openV = row[def.open], closeV = row[def.close];
    const showClose = typeof closeV==="number" && closeV!==0;
    const csgDiff = typeof row.CsgAtOpen==="number" && typeof openV==="number" && def.id!=="csg" ? row.CsgAtOpen-openV : null;
    return {
      title:cycleTitle(row), sub:row.time, field:def.open, value:openV, unit:"psi", cycle:row.index,
      lines:[
        { label:`${def.title.split(" ")[0]} @ open`, value:r1(openV), unit:"psi", color:S[def.oc] },
        ...(showClose ? [{ label:`${def.title.split(" ")[0]} @ close`, value:r1(closeV), unit:"psi", color:S[def.cc] }] : []),
        ...(def.id==="csg" ? [{ label:"Buildup in shut-in", value:r2(row.CsgBuildup), unit:"psi", color:S.amber }] : []),
        ...(csgDiff!=null ? [{ label:"Casing − "+def.title.split(" ")[0]+" @ open", value:r1(csgDiff), unit:"psi" }] : []),
        { label:"Flow rate", value:r1(row.FlowRateAtClose), unit:"MCF/d", color:S.green },
        ...stateLine(row),
      ],
      context:{ chart:def.title, ...num(row,["CsgAtOpen","TbgAtOpen","LineAtOpen","CsgAtClose","LineAtClose","CsgBuildup","TimeOffTotal","FlowRateAtClose"]) },
      fields:[fld("CsgAtOpen","Casing @ open",row.CsgAtOpen,"psi"), fld("TbgAtOpen","Tubing @ open",row.TbgAtOpen,"psi"),
              fld("LineAtOpen","Line @ open",row.LineAtOpen,"psi"), fld("CsgAtClose","Casing @ close",row.CsgAtClose,"psi"),
              fld("CsgBuildup","Casing buildup in shut-in",row.CsgBuildup,"psi"), fld("TimeOffTotal","Shut-in time",row.TimeOffTotal,"min")].filter(Boolean),
      status:row._status,
    };
  };
  const tipEff = row => ({
    title:cycleTitle(row), sub:row.time, field:"TimeOffTotal", value:row.TimeOffTotal, unit:"min", cycle:row.index,
    lines:[
      { label:"Casing buildup", value:r2(row.CsgBuildup), unit:"psi", color:phaseColor(row._phase) },
      { label:"Buildup rate", value:r2(row.BuildupRate), unit:"psi/min" },
      { label:"Shut-in time", value:r1(row.TimeOffTotal), unit:"min", color:S.pink },
      ...stateLine(row),
    ],
    context:{ chart:"Shut-in effectiveness", ...num(row,["TimeOffTotal","CsgBuildup","BuildupRate","CsgAtOpen","CsgAtClose","FlowRateAtClose"]) },
    fields:[fld("TimeOffTotal","Shut-in time",row.TimeOffTotal,"min"), fld("CsgBuildup","Casing buildup",row.CsgBuildup,"psi"),
            fld("BuildupRate","Buildup rate",row.BuildupRate,"psi/min"), fld("CsgAtOpen","Casing @ open",row.CsgAtOpen,"psi")].filter(Boolean),
    status:row._status,
  });
  const tipHist = (def,total) => row => {
    const lo = Number(row.bin), hi = lo + binW, mid = lo + binW/2;
    const pct = total ? Math.round((row.count/total)*1000)/10 : 0;
    return {
      title:`${def.title} pressure @ open`, sub:`≈ ${lo.toFixed(0)}, ${hi.toFixed(0)} psi`, field:def.open, value:r1(mid), unit:"psi", cycle:null,
      lines:[
        { label:"Cycles in this range", value:row.count, color:S[def.accent] },
        { label:"Share of all cycles", value:`${pct}%` },
      ],
      context:{ chart:`${def.title} pressure distribution at open`, bin_mid_psi:r1(mid), cycles_in_bin:row.count, share_pct:pct, total_cycles:total },
      fields:[fld(def.open,`${def.title} pressure @ open (bin mid)`,r1(mid),"psi"), fld("cycles_in_bin","Cycles in this bin",row.count,"cycles"), fld("share_pct","Share of all cycles",pct,"%")].filter(Boolean),
    };
  };

  /* ── table data ── */
  const stageRows = [
    { stage:"Shut-in",      key:"_shutMin",  color:S.amber, what:"Valve closed, casing builds pressure." },
    { stage:"Plunger rise", key:"_riseMin",  color:S.cyan,  what:"Plunger and liquid slug travel up (ArrivalMinute)." },
    { stage:"After-flow",   key:"_afterMin", color:S.green, what:"Gas produced after plunger arrival (TimeAfterFlow)." },
  ].map(r=>{ const a = ctx.map(d=>d[r.key]); return { ...r, avg:avg(a), median:med(a), min:a.length?Math.min(...a):null, max:a.length?Math.max(...a):null, share: totalTime ? sum(a)/totalTime*100 : null }; });

  const longest = [...ctx].filter(d=>d._shutMin>0).sort((a,b)=>b._shutMin-a._shutMin).slice(0,8);

  const dAvg = (x,y)=>avg(ctx.filter(d=>typeof d[x]==="number" && typeof d[y]==="number").map(d=>d[x]-d[y]));
  const DIFFS = { csg:["CsgAtOpen","TbgAtOpen","Casing − Tubing"], tbg:["TbgAtOpen","LineAtOpen","Tubing − Line"], line:["CsgAtOpen","LineAtOpen","Casing − Line"] };
  const pressStats = ROWS.map(def=>{
    const o = vals(ctx,def.open), c = vals(ctx,def.close).filter(v=>v!==0);
    const [dx,dy,dl] = DIFFS[def.id];
    return { id:def.id, name:def.title.split(" ")[0], color:S[def.oc],
      mean:avg(o), median:med(o), min:o.length?Math.min(...o):null, max:o.length?Math.max(...o):null,
      closeMean:avg(c), drop:(o.length&&c.length)?avg(o)-avg(c):null, diff:dAvg(dx,dy), diffLabel:dl };
  });

  const detailFor = r => <CycleDetail row={r} prev={ctx[ctx.findIndex(d=>d.index===r.index)-1]} medShut={medShut}/>;

  const stateBadge = p => (
    <span style={{ display:"inline-flex", alignItems:"center", gap:6, color:phaseColor(p) }}>
      <Swatch block color={phaseColor(p)}/>{PHASE_LABEL[p]}
    </span>
  );
  const alertCols = [
    { k:"cycle", label:"Cycle", render:r=><b style={{ color:phaseColor(r._phase) }}>{sel===r.index?"▾":"▸"} {cycleTickFmt(r.index)}</b> },
    { k:"time", label:"Time", render:r=>r.time },
    { k:"state", label:"State", render:r=>stateBadge(r._phase) },
    { k:"shut", label:"Shut-in (min)", align:"right", render:r=>fmt(r.TimeOffTotal) },
    { k:"bu", label:"Buildup (psi)", align:"right", render:r=>fmt(r.CsgBuildup,2) },
    { k:"rate", label:"Rate (psi/min)", align:"right", render:r=>fmt(r.BuildupRate,3) },
  ];

  /* ═════════ 1 · CYCLE TIMELINE ═════════ */
  const timeline = (
    <div style={{ display:"grid", gap:24 }}>
      <div style={{ padding:"16px 20px", background:C.card, border:`1px solid ${C.border}`, borderRadius:12, fontFamily:MONO, fontSize:10.5, color:C.muted, lineHeight:1.8 }}>
        <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text, marginBottom:4 }}>Overview</div>
        Each chart is overlaid with the cycle-stage profile. The coloured column behind every cycle shows how its time was divided between <b style={{ color:C.text }}>shut-in, plunger rise and after-flow</b>, giving pressure and flow readings operating context.
        <div style={{ display:"flex", gap:16, flexWrap:"wrap", alignItems:"center", marginTop:10 }}>
          <span style={{ color:C.text }}>Background per cycle:</span><StageKeys/>
          <Key><Swatch block color={S.red}/>Red / purple tint = cycle flagged by a shut-in rule</Key>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:14 }}>
        <Kpi label="Cycles" value={ctx.length} color={S.cyan} sub="in this window"/>
        <Kpi label="Normal" value={ctx.length?Math.round(n("ok")/ctx.length*100):0} unit="%" color={S.green} sub={`${n("ok")} cycles passed all checks`}/>
        <Kpi label="Flattening" value={n("flat")} color={S.purple} sub="warning cycles"/>
        <Kpi label="Negative buildup" value={n("negative")} color={S.red} sub="critical cycles"/>
        <Kpi label="Median shut-in" value={fmt(medShut,1)} unit="min" color={S.pink} sub="typical shut-in"/>
        <Kpi label="Total gas" value={fmt(ctx[ctx.length-1]?.CumVol,1)} unit="MCF" color={S.amber} sub="total produced in this window"/>
      </div>

      <ChartCard title="Casing, Tubing & Line Pressure at Valve Open"
        subtitle="Pressure at valve open, background columns = shut-in / plunger rise / after-flow per cycle"
        accentColor={S.cyan} height={HERO_H + 28 + 48 + BAND_LEG_H} details={CHART_DETAILS.hero} grow
        right={<Seg value={flowMode} onChange={setFlowMode} opts={[{id:"stacked",label:"Separate flow panel"},{id:"overlay",label:"Secondary axis"}]}/>}>
        <RowKey>
          <Key><Swatch color={S.cyan}/>Casing @ open</Key>
          <Key><Swatch color={S.teal}/>Tubing @ open</Key>
          <Key><Swatch color={S.blue}/>Line @ open</Key>
          {flowMode==="overlay" && <Key><Swatch block color={S.green}/>Flow rate (MCF/d) · right axis</Key>}
          {flowMode==="overlay" && <TrendKeys/>}
        </RowKey>
        <div data-pchart="hero">
          <ResponsiveContainer width="100%" height={HERO_H}>
            <ComposedChart data={ctx} margin={ROW_MARGIN} syncId="pressctx" barCategoryGap={0}>
              {flowGrad("gflow-hero")}
              {gridEl}{xAxisEl}{dateAxisEl}
              <YAxis yAxisId="psi" width={AXIS_W} label={yLbl("Pressure (psi)", C.muted)} domain={Y_PAD} allowDecimals={false} tickFormatter={v=>Math.round(v)} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
              {flowMode==="overlay" && <YAxis yAxisId="flow" orientation="right" width={AXIS_W} label={yLbl("Flow rate (MCF/d)", S.green, true)} domain={[0,"auto"]} tick={{ fill:S.green, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>}
              {bandAxis}
              <Tooltip content={<AdviceTip chartId="hero" build={tipHero}/>} cursor={cursor}/>
              {bandBar}
              {flowMode==="overlay" && trendAreas(flowTrends)}
              {flowMode==="overlay" && tsCols.includes("FlowRateAtClose") && <Area yAxisId="flow" type="stepAfter" dataKey="FlowRateAtClose" name="Flow rate (MCF/d)" stroke={S.green} strokeOpacity={0.5} strokeWidth={1} fill="url(#gflow-hero)" dot={false} activeDot={false} isAnimationActive={false}/>}
              {tsCols.includes("LineAtOpen") && <Line yAxisId="psi" type="monotone" dataKey="LineAtOpen" name="Line @ open" stroke={S.blue} strokeWidth={1.3} dot={false} isAnimationActive={false}/>}
              {tsCols.includes("TbgAtOpen") && <Line yAxisId="psi" type="monotone" dataKey="TbgAtOpen" name="Tubing @ open" stroke={S.teal} strokeWidth={1.6} dot={false} isAnimationActive={false}/>}
              {tsCols.includes("CsgAtOpen") && <Line yAxisId="psi" type="monotone" dataKey="CsgAtOpen" name="Casing @ open" stroke={S.cyan} strokeWidth={2} dot={false} isAnimationActive={false}/>}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {flowMode === "overlay" && <TrendList trends={flowTrends} unit="MCF/d"/>}
        <BandLegend/>
      </ChartCard>

      {flowMode==="stacked" && (
        <ChartCard title="Production: Flow Rate" subtitle="FlowRateAtClose (MCF/d), same background columns as above, the ribbon at the top marks detected production increases and decreases"
          accentColor={S.green} height={MID_H + 28 + 48 + BAND_LEG_H} details={CHART_DETAILS.flow} grow>
          <RowKey><Key><Swatch block color={S.green}/>Flow rate (MCF/d)</Key><TrendKeys/></RowKey>
          <div data-pchart="flow">
            <ResponsiveContainer width="100%" height={MID_H}>
              <ComposedChart data={ctx} margin={ROW_MARGIN} syncId="pressctx" barCategoryGap={0}>
                {flowGrad("gflow-panel")}
                {gridEl}{xAxisEl}{dateAxisEl}
                <YAxis yAxisId="flow" width={AXIS_W} label={yLbl("Flow rate (MCF/d)", S.green)} domain={[0,"auto"]} tick={{ fill:S.green, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
                {bandAxis}
                <Tooltip content={<AdviceTip chartId="flow" build={tipProd(flowTrends,"MCF/d")}/>} cursor={cursor}/>
                {bandBar}
                {trendAreas(flowTrends)}
                {tsCols.includes("FlowRateAtClose") && <Area yAxisId="flow" type="stepAfter" dataKey="FlowRateAtClose" name="Flow rate (MCF/d)" stroke={S.green} strokeWidth={1.6} fill="url(#gflow-panel)" dot={false} isAnimationActive={false}/>}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <TrendList trends={flowTrends} unit="MCF/d"/>
          <BandLegend/>
        </ChartCard>
      )}

      <ChartCard title="Production: Total Gas Produced Over Time" subtitle="Running total of VolCycleTotal (MCF), the slope is production per cycle, the ribbon at the top marks where production per cycle increases or decreases"
        accentColor={S.amber} height={MID_H + 28 + 48 + BAND_LEG_H} details={CHART_DETAILS.cum} grow>
        <RowKey><Key><Swatch color={S.amber}/>Total gas produced (MCF)</Key><TrendKeys/></RowKey>
        <div data-pchart="cum">
          <ResponsiveContainer width="100%" height={MID_H}>
            <ComposedChart data={ctx} margin={ROW_MARGIN} syncId="pressctx" barCategoryGap={0}>
              {gridEl}{xAxisEl}{dateAxisEl}
              <YAxis yAxisId="cum" width={AXIS_W} label={yLbl("Total gas produced (MCF)", S.amber)} domain={[0,"auto"]} tick={{ fill:S.amber, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
              {bandAxis}
              <Tooltip content={<AdviceTip chartId="cum" build={tipProd(volTrends,"MCF")}/>} cursor={cursor}/>
              {bandBar}
              {trendAreas(volTrends)}
              <Area yAxisId="cum" type="monotone" dataKey="CumVol" name="Total gas produced (MCF)" stroke={S.amber} strokeWidth={1.8} fill={S.amber} fillOpacity={0.12} dot={false} isAnimationActive={false}/>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <TrendList trends={volTrends} unit="MCF"/>
        <BandLegend/>
      </ChartCard>
    </div>
  );

  /* ═════════ 2 · CYCLE STAGES ═════════ */
  const stages = (
    <div style={{ display:"grid", gap:24 }}>
      <ChartCard title="Cycle Stages: Shut-in · Plunger Rise · After-flow"
        subtitle="Share of each cycle from TimeOffTotal, ArrivalMinute and TimeAfterFlow, a growing shut-in share indicates the well is being held shut longer"
        accentColor={S.amber} height={MID_H + 52 + BAND_LEG_H} details={CHART_DETAILS.stage} grow>
        <RowKey><StageKeys/></RowKey>
        <div data-pchart="stage">
          <ResponsiveContainer width="100%" height={MID_H + 24}>
            <AreaChart data={ctx} margin={ROW_MARGIN} syncId="pressctx" stackOffset="expand">
              {gridEl}{xAxisEl}{dateAxisEl}
              <YAxis width={AXIS_W} label={yLbl("Share of cycle (%)", C.muted)} tickFormatter={v=>`${Math.round(v*100)}%`} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
              <Tooltip content={<AdviceTip chartId="stage" build={tipStage}/>} cursor={cursor}/>
              <Area type="stepAfter" dataKey="_shutMin"  stackId="stage" name="Shut-in"      stroke={S.amber} strokeWidth={1.4} fill={S.amber} fillOpacity={0.16} dot={false} isAnimationActive={false}/>
              <Area type="stepAfter" dataKey="_riseMin"  stackId="stage" name="Plunger rise" stroke={S.cyan}  strokeWidth={1.4} fill={S.cyan}  fillOpacity={0.16} dot={false} isAnimationActive={false}/>
              <Area type="stepAfter" dataKey="_afterMin" stackId="stage" name="After-flow"   stroke={S.green} strokeWidth={1.4} fill={S.green} fillOpacity={0.16} dot={false} isAnimationActive={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <BandLegend/>
      </ChartCard>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(380px, 1fr))", gap:24 }}>
        <ChartCard title="Stage summary" subtitle="Mean, minimum and maximum duration and share of total cycle time for each stage" accentColor={S.cyan} height="auto">
          <DataTable rowKey={r=>r.stage} rows={stageRows} maxH={300} cols={[
            { k:"stage", label:"Stage", render:r=><span style={{ display:"inline-flex", alignItems:"center", gap:6 }}><Swatch block color={r.color}/>{r.stage}</span> },
            { k:"avg", label:"Mean (min)", align:"right", render:r=>fmt(r.avg) },
            { k:"min", label:"Min (min)", align:"right", render:r=>fmt(r.min) },
            { k:"max", label:"Max (min)", align:"right", render:r=>fmt(r.max) },
            { k:"share", label:"Share of time", align:"right", render:r=>`${fmt(r.share,1)}%` },
            { k:"what", label:"Description", render:r=>r.what },
          ]}/>
        </ChartCard>
        <ChartCard title="Longest shut-ins" subtitle={`Top 8 cycles ranked by total shut-in duration (reported in minutes). "vs typical" compares each cycle with the median shut-in of ${fmt(medShut,1)} min across all cycles. Select a row for details.`} accentColor={S.amber} height="auto">
          <DataTable rowKey={r=>r.index} rows={longest} activeKey={sel} onRow={r=>setSel(sel===r.index?null:r.index)} renderExpanded={r=>detailFor(r)} maxH={420} cols={[
            { k:"cycle", label:"Cycle", render:r=><b>{sel===r.index?"▾":"▸"} {cycleTickFmt(r.index)}</b> },
            { k:"time", label:"Time", render:r=>r.time },
            { k:"shut", label:"Shut-in duration (min)", align:"right", render:r=>fmt(r.TimeOffTotal) },
            { k:"x", label:"vs typical shut-in", align:"right", render:r=>medShut?`${fmt(r.TimeOffTotal/medShut,1)}×`:"-" },
            { k:"st", label:"State", render:r=>stateBadge(r._phase) },
          ]}/>
        </ChartCard>
      </div>
    </div>
  );

  /* ═════════ 3 · SHUT-IN HEALTH ═════════ */
  const health = (
    <div style={{ display:"grid", gap:24 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:14 }}>
        <Kpi label="Critical" value={n("negative")} color={S.red} sub="negative buildup" tip="Casing pressure fell during shut-in"/>
        <Kpi label="Warning" value={n("flat")} color={S.purple} sub="flattened + long shut-in"/>
        <Kpi label="Normal" value={n("ok")} color={S.green} sub="passed all checks"/>
        <Kpi label="Lost shut-in time" value={fmt(lostMin,0)} unit="min" color={S.pink} sub="beyond median, warning cycles"/>
        <Kpi label="Avg buildup rate" value={fmt(avg(vals(ctx,"BuildupRate")),2)} unit="psi/min" color={S.cyan} sub="all cycles"/>
      </div>

      <ChartCard title="Casing Buildup per Cycle" subtitle="Casing psi gained during shut-in, red bars indicate negative buildup" accentColor={S.cyan} height={MID_H + 28} details={CHART_DETAILS.buildup}>
        <RowKey><Key><Swatch block color={S.cyan}/>Normal</Key><Key><Swatch block color={S.purple}/>Flattening</Key><Key><Swatch block color={S.red}/>Negative</Key><Key><Swatch color={S.red} dashed/>Negative buildup limit</Key></RowKey>
        <div data-pchart="eff">
          <ResponsiveContainer width="100%" height={MID_H}>
            <ComposedChart data={ctx} margin={ROW_MARGIN} syncId="healthctx" barCategoryGap={1}>
              {gridEl}{xAxisEl}{dateAxisEl}
              <YAxis yAxisId="psi" width={AXIS_W} label={yLbl("Casing buildup (psi)", C.muted)} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
              <Tooltip content={<AdviceTip chartId="eff" build={tipEff}/>} cursor={{ fill:`${C.muted}22` }}/>
              <ReferenceLine yAxisId="psi" y={0} stroke={C.muted} strokeDasharray="2 2"/>
              <ReferenceLine yAxisId="psi" y={rules.neg} stroke={S.red} strokeDasharray="5 3"/>
              <Bar yAxisId="psi" dataKey="CsgBuildup" name="Casing buildup (psi)" radius={[3,3,0,0]} isAnimationActive={false}>
                {ctx.map((d,i)=><Cell key={i} fill={d._phase==="ok"?S.cyan:phaseColor(d._phase)} fillOpacity={d._phase==="ok"?0.7:0.9}/>)}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      <ChartCard title="Buildup Rate" subtitle="psi gained per minute of shut-in, points under the amber line count as flattened (if the shut-in was also long)" accentColor={S.amber} height={MID_H + 28 + BAND_LEG_H} details={CHART_DETAILS.rate} grow>
        <RowKey><Key><Swatch color={S.cyan}/>Buildup rate (psi/min)</Key><Key><Swatch color={S.amber} dashed/>Flat-rate limit</Key></RowKey>
        <div data-pchart="rate">
          <ResponsiveContainer width="100%" height={MID_H}>
            <ComposedChart data={ctx} margin={ROW_MARGIN} syncId="healthctx" barCategoryGap={0}>
              {gridEl}{xAxisEl}{dateAxisEl}
              <YAxis yAxisId="rate" width={AXIS_W} label={yLbl("Buildup rate (psi/min)", C.muted)} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
              {bandAxis}
              <Tooltip content={<AdviceTip chartId="rate" build={tipEff}/>} cursor={cursor}/>
              {flagBar}
              <ReferenceLine yAxisId="rate" y={0} stroke={C.muted} strokeDasharray="2 2"/>
              <ReferenceLine yAxisId="rate" y={rules.flat} stroke={S.amber} strokeDasharray="5 3"/>
              <Line yAxisId="rate" type="monotone" dataKey="BuildupRate" name="Buildup rate (psi/min)" stroke={S.cyan} strokeWidth={1.8} dot={false} isAnimationActive={false}/>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <BandLegend tintOnly/>
      </ChartCard>

      <ChartCard title="Shut-in Time" subtitle="Minutes shut in per cycle vs the median, above the amber line = extended shut-in" accentColor={S.pink} height={MID_H + 28 + BAND_LEG_H} details={CHART_DETAILS.shut} grow>
        <RowKey><Key><Swatch color={S.pink}/>Shut-in time (min)</Key><Key><Swatch color={C.muted} dashed/>Median ({fmt(medShut,1)} min)</Key><Key><Swatch color={S.amber} dashed/>Long shut-in limit</Key></RowKey>
        <div data-pchart="shut">
          <ResponsiveContainer width="100%" height={MID_H}>
            <ComposedChart data={ctx} margin={ROW_MARGIN} syncId="healthctx" barCategoryGap={0}>
              {gridEl}{xAxisEl}{dateAxisEl}
              <YAxis yAxisId="min" width={AXIS_W} label={yLbl("Shut-in time (min)", C.muted)} domain={[0,"auto"]} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
              {bandAxis}
              <Tooltip content={<AdviceTip chartId="shut" build={tipEff}/>} cursor={cursor}/>
              {flagBar}
              {medShut!=null && <ReferenceLine yAxisId="min" y={medShut} stroke={C.muted} strokeDasharray="3 3"/>}
              {medShut!=null && <ReferenceLine yAxisId="min" y={medShut*rules.longX} stroke={S.amber} strokeDasharray="5 3"/>}
              <Line yAxisId="min" type="monotone" dataKey="TimeOffTotal" name="Shut-in time (min)" stroke={S.pink} strokeWidth={1.8} dot={false} isAnimationActive={false}/>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <BandLegend tintOnly/>
      </ChartCard>

      <ChartCard title="Alerts: flagged cycles" subtitle={`${flagged.length} cycle${flagged.length===1?"":"s"} flagged, select a row for cycle factors and an AI recommendation`} accentColor={n("negative")?S.red:S.amber} height="auto">
        <DataTable rowKey={r=>r.index} rows={[...flagged].reverse()} activeKey={sel} onRow={r=>setSel(sel===r.index?null:r.index)} renderExpanded={r=>detailFor(r)} maxH={520} cols={alertCols}/>
        <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginTop:10 }}>Alerts are displayed in the dashboard only. E-mail and controller notifications require backend integration.</div>
      </ChartCard>
    </div>
  );

  /* ═════════ 4 · PRESSURE PROFILES ═════════ */
  const pdef = ROWS.find(r=>r.id===pk) || ROWS[0];
  const pOpenC = S[pdef.oc], pCloseC = S[pdef.cc];
  const pHasOpen  = tsCols.includes(pdef.open);
  const pHasClose = tsCols.includes(pdef.close) && ctx.some(d=>typeof d[pdef.close]==="number" && d[pdef.close]!==0);
  const profiles = (
    <div style={{ display:"grid", gap:24 }}>
      <ChartCard title={`${pdef.title}: ${pHasClose?"At Open vs At Close":"At Open"}`}
        subtitle={`${pdef.open}${pHasClose?`, ${pdef.close}`:""}, ${pdef.desc}`}
        accentColor={pOpenC} height={HERO_H + 28 + BAND_LEG_H} details={CHART_DETAILS.press}
        right={<Seg value={pk} onChange={setPk} opts={ROWS.map(r=>({ id:r.id, label:r.title.split(" ")[0] }))}/>}>
        <RowKey>
          <Key><Swatch color={pOpenC}/>At open</Key>
          {pHasClose ? <Key><Swatch color={pCloseC} dashed/>At close</Key> : <span style={{ opacity:0.75 }}>At close reads 0 psi on every cycle, not plotted</span>}
        </RowKey>
        <div data-pchart={pdef.id}>
          <ResponsiveContainer width="100%" height={HERO_H}>
            <ComposedChart data={ctx} margin={ROW_MARGIN} syncId="profctx" barCategoryGap={0}>
              {gridEl}{xAxisEl}{dateAxisEl}
              <YAxis yAxisId="psi" width={AXIS_W} label={yLbl(`${pdef.title.split(" ")[0]} pressure (psi)`, C.muted)} domain={Y_PAD} allowDecimals={false} tickFormatter={v=>Math.round(v)} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
              {bandAxis}
              <Tooltip content={<AdviceTip chartId={pdef.id} build={tipPress(pdef)}/>} cursor={cursor}/>
              {bandBar}
              {pHasClose && <Line yAxisId="psi" type="monotone" dataKey={pdef.close} name={pdef.close} stroke={pCloseC} strokeWidth={1.5} strokeDasharray="4 2" dot={false} isAnimationActive={false}/>}
              {pHasOpen  && <Line yAxisId="psi" type="monotone" dataKey={pdef.open}  name={pdef.open}  stroke={pOpenC}  strokeWidth={2} dot={false} isAnimationActive={false}/>}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <BandLegend/>
      </ChartCard>

      <ChartCard title="Pressure statistics" subtitle="Mean, median and range at valve open, with average open-to-close drop and the pressure differential at valve open" accentColor={S.cyan} height="auto">
        <DataTable rowKey={r=>r.id} rows={pressStats} maxH={260} cols={[
          { k:"name", label:"Pressure", render:r=><span style={{ display:"inline-flex", alignItems:"center", gap:6 }}><Swatch block color={r.color}/>{r.name}</span> },
          { k:"mean", label:"Mean @ open (psi)", align:"right", render:r=>fmt(r.mean) },
          { k:"median", label:"Median", align:"right", render:r=>fmt(r.median) },
          { k:"min", label:"Min", align:"right", render:r=>fmt(r.min) },
          { k:"max", label:"Max", align:"right", render:r=>fmt(r.max) },
          { k:"cm", label:"Mean @ close", align:"right", render:r=>r.closeMean==null?"Not recorded":fmt(r.closeMean) },
          { k:"drop", label:"Drop open→close", align:"right", render:r=>r.drop==null?"Not recorded":fmt(r.drop) },
          { k:"diff", label:"Differential @ open (psi)", align:"right", render:r=><span>{fmt(r.diff)}<div style={{ color:C.muted, fontSize:9 }}>{r.diffLabel}</div></span> },
        ]}/>
        <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginTop:10, lineHeight:1.6 }}>
          Differential is the mean pressure difference at valve open for the pair shown under each value.
          {pressStats.some(r=>r.closeMean==null) && ` ${pressStats.filter(r=>r.closeMean==null).map(r=>r.name).join(", ")} pressure at close reads 0 psi on every cycle, so close-based values are not recorded.`}
        </div>
      </ChartCard>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(300px, 1fr))", gap:24 }}>
        {histRows.map(({def,data,total},i)=>{
          const ac = S[def.accent];
          return (
            <ChartCard key={def.id} title={`${def.title} Pressure at Open`} subtitle={`${def.open}, ${total} cycles, same psi scale on all three`} accentColor={ac} height={260}>
              <div data-pchart={`hist-${def.id}`} style={{ height:"100%" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} margin={{ top:6, right:10, left:8, bottom:36 }} barCategoryGap={1}>
                    <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                    <XAxis dataKey="bin" tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} interval="preserveStartEnd" minTickGap={14} label={{ value:"Pressure (psi)", position:"insideBottom", offset:-30, fill:C.text, fontSize:11, fontFamily:MONO }}/>
                    <YAxis width={60} allowDecimals={false} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={{ value:"Cycles", angle:-90, position:"insideLeft", offset:0, fill:C.text, fontSize:11, fontFamily:MONO }}/>
                    <Tooltip content={<AdviceTip chartId={`hist-${def.id}`} build={tipHist(def,total)}/>} cursor={{ fill:`${C.muted}22` }}/>
                    <Bar dataKey="count" fill={ac} fillOpacity={0.85} radius={[3,3,0,0]} isAnimationActive={false}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      {/* sub-tab bar */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:8 }}>
        {PSUBS.map(t=>{
          const on = sub===t.id;
          const badge = t.id==="health" && flagged.length>0;
          return (
            <button key={t.id} onClick={()=>setSub(t.id)} style={{
              background:on?`${S.cyan}18`:C.card, color:on?S.cyan:C.muted, border:`1px solid ${on?S.cyan+"55":C.border}`,
              borderRadius:10, padding:"10px 18px", textAlign:"left", cursor:"pointer", transition:"all 0.18s ease",
            }}>
              <div style={{ fontFamily:SANS, fontWeight:700, fontSize:12, letterSpacing:0.5, display:"flex", alignItems:"center", gap:8 }}>
                {t.label}
                {badge && <span style={{ fontFamily:MONO, fontSize:9, background:n("negative")?S.red:S.purple, color:"#000", borderRadius:10, padding:"1px 7px" }}>{flagged.length}</span>}
              </div>
              <div style={{ fontFamily:MONO, fontSize:9, opacity:0.8, marginTop:2 }}>{t.sub}</div>
            </button>
          );
        })}
      </div>
      <div style={{ height:18 }}/>
      {sub==="timeline" && timeline}
      {sub==="stages"   && stages}
      {sub==="health"   && health}
      {sub==="profiles" && profiles}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   PARENT PAGE: Pressure / Velocity sub-tabs
   ══════════════════════════════════════════════════════════════════════════ */
const SUB_TABS = [
  { id:"pressure", label:"Pressure", sub:"Casing, Tubing, Line", accent:"cyan",
    icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="6" rx="3"/><circle cx="17" cy="12" r="2.5"/></svg> },
  { id:"velocity", label:"Velocity", sub:"Plunger arrival speed", accent:"amber",
    icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22a9 9 0 0 0 9-9 9 9 0 0 0-9-9 9 9 0 0 0-9 9 9 9 0 0 0 9 9z"/><line x1="12" y1="13" x2="15.5" y2="9.5"/><circle cx="12" cy="13" r="1.2" fill="currentColor"/></svg> },
];

export default function PressureVelocityPage({ tsData, tsCols, pressData, arrStats, histData, COL_COLORS, TTip }) {
  const { isDark } = useTheme();
  applyTheme(getColors(isDark), isDark);

  const [active, setActive] = useState("pressure");

  /* DATA WINDOW: every cycle in tsData carries its open time; the window trims what both tabs draw */
  const stamped = useMemo(()=>(tsData||[]).map(d=>({ ...d, _t:parseT(d.time) })),[tsData]);
  const tw = useTimeWindow(stamped);
  const view = tw.view;                                                    // "ALL" = every cycle, exactly as before
  const arrW = useMemo(()=>tw.isAll ? null : velStatsOf(view),[tw.isAll, view]);
  const arrStatsX = tw.isAll ? arrStats : arrW;                            // histograms + stats are re-binned on the windowed cycles
  const histX = tw.isAll ? histData : (arrW ? arrW.bins.map((b,i)=>({ bin:b.toFixed(0), count:arrW.counts[i], _actualFieldKey:"ArrivalVelocity" })) : []);
  const pressX = useMemo(()=>tw.isAll ? pressData : pressDistOf(view),[tw.isAll, pressData, view]);

  return (
    <>
      <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:14, marginBottom:20 }}>

        <div style={{ display:"flex", gap:6, background:C.panel, border:`1px solid ${C.border}`, borderRadius:10, padding:4 }}>
          {SUB_TABS.map(t=>{
            const on = active===t.id;
            const ac = C[t.accent];
            return (
              <button key={t.id} onClick={()=>setActive(t.id)} style={{
                background:on?`${ac}15`:"transparent",
                color:on?ac:C.muted,
                border:`1px solid ${on?ac+"44":"transparent"}`,
                borderRadius:7, padding:"8px 16px", fontFamily:SANS, fontWeight:700,
                fontSize:11, letterSpacing:1, textTransform:"uppercase", cursor:"pointer",
                display:"flex", alignItems:"center", gap:8, transition:"all 0.18s ease",
              }}
              onMouseEnter={e=>{ if(!on){e.currentTarget.style.background=`${ac}08`;e.currentTarget.style.color=C.text;} }}
              onMouseLeave={e=>{ if(!on){e.currentTarget.style.background="transparent";e.currentTarget.style.color=C.muted;} }}>
                {t.icon}
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {stamped.some(d=>d._t!=null) && (
        <div style={{ marginBottom:20 }}>
          <DataWindowBar ctl={tw} total={stamped.length} isDark={isDark}
            note="The window applies to every Pressure and Velocity chart, the pressure / velocity distributions and the velocity statistics."/>
        </div>
      )}

      {active==="pressure" && (
        <PressureTab tsData={view} tsCols={tsCols} pressData={pressX}/>
      )}
      {active==="velocity" && (
        <VelocityTab tsData={view} tsCols={tsCols} arrStats={arrStatsX} histData={histX} TTip={TTip}/>
      )}
    </>
  );
}